import { FinancialData } from '@api';
import {
  KeyStatItem,
  KeyStats,
  ValueSegment,
} from '@design/components/key-stats/KeyStats';
import { formatPercentage, getCurrencySymbol } from '@helpers';
import { useGetEarningById } from '@terminal/react-query-hooks/earnings/useGetEarningById';
import {
  formatCurrencyValue,
  formatSurpriseValue,
} from '@terminal/widgets/earnings/utils/utils';
import { useTranslation } from 'react-i18next';

export interface EarningsKeyStatsProps {
  earningsId: string | undefined;
  variant: 'earnings_preview' | 'earnings_recap';
}

const buildSegments = (
  data: FinancialData,
  currencySymbol: string,
  variant: EarningsKeyStatsProps['variant']
): ValueSegment[] => {
  if (variant === 'earnings_preview') {
    return [{ text: formatCurrencyValue(data.estimate, currencySymbol) }];
  }

  const segments: ValueSegment[] = [
    { text: formatCurrencyValue(data.value, currencySymbol) },
  ];

  if (data.surprise === undefined) {
    return segments;
  }

  const surpriseText = formatSurpriseValue(data.surprise, currencySymbol);
  const percentageText =
    data.surprise_percentage !== undefined
      ? ` (${formatPercentage(data.surprise_percentage, {
          suffix: '%',
          addPlus: true,
          useOriginalValue: true,
        })})`
      : '';

  segments.push({
    text: `${surpriseText}${percentageText}`,
    colorVariant: data.surprise > 0 ? 'bullish' : 'bearish',
  });

  return segments;
};

export const EarningsKeyStats = ({
  earningsId,
  variant,
}: EarningsKeyStatsProps) => {
  const { t } = useTranslation(['earnings']);
  const { data: earningsData } = useGetEarningById(earningsId);

  if (!earningsData) {
    return null;
  }

  const { eps, revenue, currency } = earningsData;
  const currencySymbol = getCurrencySymbol(currency);

  const epsLabel = t(
    'earnings:earningsInsight.drawer.earningsRecap.tables.eps'
  );
  const revenueLabel = t(
    'earnings:earningsInsight.drawer.earningsRecap.tables.revenue'
  );
  const estimateLabel = t(
    'earnings:earningsInsight.drawer.earningsPreview.tables.estimate'
  ).toLowerCase();
  const epsItemLabel =
    variant === 'earnings_preview'
      ? `${epsLabel} ${estimateLabel}`
      : t('earnings:earningsInsight.drawer.earningsRecap.tables.eps');
  const revenueItemLabel =
    variant === 'earnings_preview'
      ? `${revenueLabel} ${estimateLabel}`
      : revenueLabel;

  const items: KeyStatItem[] = [
    {
      label: revenueItemLabel,
      value: buildSegments(revenue, currencySymbol, variant),
    },
    {
      label: epsItemLabel,
      value: buildSegments(eps, currencySymbol, variant),
    },
  ];

  return <KeyStats items={items} itemsPerRow={2} />;
};
